"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";
import { useAppShell } from "@/context/AppShellContext";
import {
  BookOpen,
  Brain,
  ChevronDown,
  House,
  LayoutGrid,
  Library,
  Lock,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  PenLine,
  Settings,
  X,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import SessionList from "@/components/SessionList";
import { VersionBadge } from "@/components/sidebar/VersionBadge";
import { SidebarLanguageToggle } from "@/components/sidebar/SidebarLanguageToggle";
import type { SessionSummary } from "@/lib/session-api";
import { Tooltip } from "@/components/ui/Tooltip";
import { useCapabilityAccess } from "@/components/access/CapabilityAccessContext";
import type { Capability } from "@/lib/capability-routes";
import { safeLocalGet, safeLocalSet } from "@/context/app-shell-storage";

interface NavEntry {
  href: string;
  label: string;
  icon: LucideIcon;
  tooltipKey?: string;
  /** Model capability this feature needs; locked when the user lacks it. */
  requires?: Capability;
}

const PRIMARY_NAV: NavEntry[] = [
  {
    href: "/home",
    label: "Home",
    icon: House,
    tooltipKey: "Home tooltip",
    requires: "llm",
  },
  // Partners and My Agents are upstream features not used in this
  // project — excluded from the nav.
  {
    href: "/talk-note",
    label: "Co-Writer",
    icon: PenLine,
    tooltipKey: "Co-Writer tooltip",
    requires: "llm",
  },
  {
    href: "/book",
    label: "Book",
    icon: Library,
    tooltipKey: "Book tooltip",
    requires: "llm",
  },
  {
    href: "/space",
    label: "Learning Space",
    icon: LayoutGrid,
    tooltipKey: "Space tooltip",
  },
];

const SECONDARY_NAV: NavEntry[] = [
  {
    // Memory is its own top-level console (pulled out of the Learning Space):
    // a place to inspect and curate the tutor's long-term memory, not a daily
    // workspace. Never gated — memory has no per-user model requirement.
    href: "/memory",
    label: "Memory",
    icon: Brain,
    tooltipKey: "Memory tooltip",
  },
  {
    // Knowledge Center sits just above Settings: it's a console for managing
    // KBs and retrieval engines, not a daily workspace. Never gated — embedding
    // / search are shared admin infrastructure, no per-user model grant needed.
    href: "/knowledge",
    label: "Knowledge Center",
    icon: BookOpen,
    tooltipKey: "Knowledge tooltip",
  },
  { href: "/settings", label: "Settings", icon: Settings },
];
const RECENTS_COLLAPSED_KEY = "aitoktok.sidebar.recentsCollapsed";
const RECENTS_MODE_KEY = "aitoktok.sidebar.recentsMode";

interface SidebarShellProps {
  sessions?: SessionSummary[];
  activeSessionId?: string | null;
  loadingSessions?: boolean;
  showSessions?: boolean;
  /** Clicking the Chat nav item resets to a fresh session via this handler. */
  onNewChat?: () => void;
  onSelectSession?: (sessionId: string) => void | Promise<void>;
  onRenameSession?: (sessionId: string, title: string) => void | Promise<void>;
  onDeleteSession?: (sessionId: string) => void | Promise<void>;
  /**
   * Footer content rendered below the nav. Pass a render function to receive
   * the current ``collapsed`` state so footer items (e.g. Admin / Sign out) can
   * switch to their icon-only variant when the rail is collapsed.
   */
  footerSlot?: ReactNode | ((collapsed: boolean) => ReactNode);
}

export function SidebarShell({
  sessions = [],
  activeSessionId = null,
  loadingSessions = false,
  showSessions = false,
  onNewChat,
  onSelectSession,
  onRenameSession,
  onDeleteSession,
  footerSlot,
}: SidebarShellProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { t } = useTranslation();
  const { has } = useCapabilityAccess();
  const {
    sidebarCollapsed: collapsed,
    setSidebarCollapsed: setCollapsed,
    activeKnowledge,
    setActiveKnowledge,
    recentKnowledge,
  } = useAppShell();

  const navLocked = (item: NavEntry) =>
    item.requires ? !has(item.requires) : false;
  const lockedTooltip = t("Locked — contact your administrator to get access.");
  // The drawer body always renders the expanded footer (icon+label), never the
  // collapsed rail's icon-only variant.
  const drawerFooter =
    typeof footerSlot === "function" ? footerSlot(false) : footerSlot;
  const railFooter =
    typeof footerSlot === "function" ? footerSlot(collapsed) : footerSlot;
  const [recentsCollapsed, setRecentsCollapsed] = useState(false);
  // Recents shows either the books you opened last or the chat history.
  // Books is the default: chats from different books otherwise blur together.
  const [recentsMode, setRecentsMode] = useState<"books" | "chats">("books");
  // Mobile only: the topbar's slide-in nav panel. `collapsed` is a desktop
  // concept and is ignored here.
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Hydrate Recents collapse from localStorage after first render to stay SSR-safe.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRecentsCollapsed(
      safeLocalGet(RECENTS_COLLAPSED_KEY) === "1",
    );
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRecentsMode(
      safeLocalGet(RECENTS_MODE_KEY) === "chats" ? "chats" : "books",
    );
  }, []);

  const pickRecentsMode = (mode: "books" | "chats") => {
    setRecentsMode(mode);
    safeLocalSet(RECENTS_MODE_KEY, mode);
  };

  // Close the mobile drawer whenever the route changes so a nav tap dismisses it.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDrawerOpen(false);
  }, [pathname]);

  const toggleRecents = () => {
    setRecentsCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== "undefined") {
        safeLocalSet(RECENTS_COLLAPSED_KEY, next ? "1" : "0");
      }
      return next;
    });
  };

  const handleHomeClick = (event: React.MouseEvent) => {
    // Always reset to a fresh session (mirrors the old "New Chat" affordance);
    // let modifier-clicks fall through to default Link behavior so middle-click
    // open-in-new-tab still works.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)
      return;
    event.preventDefault();
    onNewChat?.();
    router.push("/home");
  };

  /**
   * The full labeled sidebar body — reused verbatim by the desktop expanded
   * rail and the mobile drawer. Only the header's top-right control differs:
   * the desktop rail collapses, the drawer closes.
   */
  const renderExpandedBody = (inDrawer: boolean) => (
    <>
      {/* Header: logo + collapse/close control */}
      <div className="flex h-14 items-center justify-between px-4">
        <Link href="/" className="group flex items-center gap-1.5">
          <Image
            src="/logo-v2.png"
            alt="AI TokTok"
            width={22}
            height={22}
            className="h-[22px] w-[22px] transition-transform duration-200 group-hover:scale-105"
          />
          <span className="bg-gradient-to-r from-[var(--foreground)] to-[var(--primary)] bg-clip-text text-[17px] font-semibold tracking-tight text-transparent transition-transform duration-200 group-hover:scale-105">
            AI TokTok
          </span>
        </Link>
        {inDrawer ? (
          <button
            onClick={() => setDrawerOpen(false)}
            className="rounded-md p-1 text-[var(--muted-foreground)] transition-colors hover:text-[var(--foreground)]"
            aria-label={t("Close menu")}
          >
            <X size={18} />
          </button>
        ) : (
          <button
            onClick={() => setCollapsed(true)}
            className="rounded-md p-1 text-[var(--muted-foreground)] transition-colors hover:text-[var(--foreground)]"
            aria-label={t("Collapse sidebar")}
          >
            <PanelLeftClose size={15} />
          </button>
        )}
      </div>

      {/* Primary nav */}
      <nav className="px-2 pt-1">
        <div className={inDrawer ? "space-y-px" : "space-y-1.5"}>
          {PRIMARY_NAV.map((item) => {
            const active = pathname.startsWith(item.href);
            const locked = navLocked(item);
            if (locked) {
              return (
                <Tooltip
                  key={item.href}
                  label={t(item.label)}
                  description={lockedTooltip}
                  side="right"
                >
                  <div
                    aria-label={`${t(item.label)} — ${lockedTooltip}`}
                    aria-disabled
                    className={`flex cursor-not-allowed items-center gap-2.5 px-3 text-[13.5px] text-[var(--muted-foreground)]/40 ${
                      inDrawer ? "rounded-lg py-2" : "h-11 rounded-lg"
                    }`}
                  >
                    <item.icon
                      size={inDrawer ? 16 : 21}
                      strokeWidth={inDrawer ? 1.5 : 1.75}
                    />
                    <span>{t(item.label)}</span>
                    <Lock size={13} strokeWidth={1.8} className="ml-auto" />
                  </div>
                </Tooltip>
              );
            }
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={item.href === "/home" ? handleHomeClick : undefined}
                className={`${
                  inDrawer
                    ? "flex items-center gap-2.5 rounded-lg px-3 py-2"
                    : "relative flex h-11 items-center gap-2.5 rounded-lg px-3"
                } text-[13.5px] transition-colors ${
                  active
                    ? inDrawer
                      ? "bg-[var(--accent)] font-medium text-[var(--foreground)]"
                      : "bg-[var(--secondary)] font-medium text-[var(--primary)] before:absolute before:-left-2 before:top-1/2 before:h-[22px] before:w-[3px] before:-translate-y-1/2 before:rounded-r-[3px] before:bg-[var(--primary)] before:content-['']"
                    : "text-[var(--foreground)]/85 hover:bg-[var(--background)]/60 hover:text-[var(--foreground)]"
                }`}
              >
                <item.icon
                  size={inDrawer ? 16 : 21}
                  strokeWidth={inDrawer ? (active ? 1.9 : 1.5) : 1.75}
                />
                <span>{t(item.label)}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Chat history — its own region below the nav, takes remaining height */}
      {showSessions && onSelectSession && onRenameSession && onDeleteSession ? (
        <section
          className={`mt-4 flex min-h-0 flex-col ${
            recentsCollapsed ? "" : "flex-1"
          }`}
        >
          <button
            type="button"
            onClick={toggleRecents}
            className="group/recents mx-2 flex items-center justify-between rounded-md px-2 py-1 text-left text-[13.5px] font-normal text-[var(--muted-foreground)]/60 transition-colors hover:bg-[var(--background)]/40 hover:text-[var(--muted-foreground)]"
            aria-expanded={!recentsCollapsed}
            aria-label={
              recentsCollapsed
                ? (t("Show recents") as string)
                : (t("Hide recents") as string)
            }
          >
            <span>{t("Recents")}</span>
            <ChevronDown
              size={13}
              strokeWidth={1.7}
              className={`transition-all duration-200 ${
                recentsCollapsed
                  ? "-rotate-90 opacity-60"
                  : "rotate-0 opacity-0 group-hover/recents:opacity-60"
              }`}
            />
          </button>
          {!recentsCollapsed && (
            <>
              <div className="mx-2 mt-1 flex gap-1">
                {(["books", "chats"] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => pickRecentsMode(mode)}
                    className={`flex-1 rounded-md px-2 py-1 text-[13.5px] transition-colors ${
                      recentsMode === mode
                        ? "bg-[var(--background)] text-[var(--foreground)]"
                        : "text-[var(--muted-foreground)]/70 hover:text-[var(--muted-foreground)]"
                    }`}
                  >
                    {mode === "books" ? t("Recent books") : t("Recent chats")}
                  </button>
                ))}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2 pt-0.5">
                {recentsMode === "chats" ? (
                  <SessionList
                    sessions={sessions}
                    activeSessionId={activeSessionId}
                    loading={loadingSessions}
                    onSelect={onSelectSession}
                    onRename={onRenameSession}
                    onDelete={onDeleteSession}
                    compact
                  />
                ) : recentKnowledge.length === 0 ? (
                  <p className="px-2 py-2 text-[13.5px] text-[var(--muted-foreground)]/60">
                    {t("No books opened yet")}
                  </p>
                ) : (
                  <ul className="space-y-0.5">
                    {recentKnowledge.map((name) => (
                      <li key={name}>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveKnowledge(name);
                            router.push("/home");
                          }}
                          className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13.5px] transition-colors ${
                            activeKnowledge === name
                              ? "bg-[var(--background)] text-[var(--foreground)]"
                              : "text-[var(--muted-foreground)] hover:bg-[var(--background)]/60 hover:text-[var(--foreground)]"
                          }`}
                        >
                          <BookOpen size={13} strokeWidth={1.7} className="shrink-0" />
                          <span className="truncate">{name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </section>
      ) : null}

      {/* When recents is collapsed or unavailable, fill the gap above the footer. */}
      {(!showSessions ||
        !onSelectSession ||
        !onRenameSession ||
        !onDeleteSession ||
        recentsCollapsed) && <div className="flex-1" />}

      {/* Secondary nav + footer */}
      <div
        className={`border-t border-[var(--border)]/40 px-2 py-2 ${
          inDrawer ? "" : "space-y-1.5"
        }`}
      >
        {SECONDARY_NAV.map((item) => {
          const active = pathname.startsWith(item.href);
          return (
            <Link
              key={item.href}
              href={item.href}
              className={`${
                inDrawer
                  ? "flex items-center gap-2.5 rounded-lg px-3 py-2"
                  : "relative flex h-11 items-center gap-2.5 rounded-lg px-3"
              } text-[13.5px] transition-colors ${
                active
                  ? inDrawer
                    ? "bg-[var(--accent)] font-medium text-[var(--foreground)]"
                    : "bg-[var(--secondary)] font-medium text-[var(--primary)] before:absolute before:-left-2 before:top-1/2 before:h-[22px] before:w-[3px] before:-translate-y-1/2 before:rounded-r-[3px] before:bg-[var(--primary)] before:content-['']"
                  : "text-[var(--foreground)]/85 hover:bg-[var(--background)]/60 hover:text-[var(--foreground)]"
              }`}
            >
              <item.icon
                size={inDrawer ? 16 : 21}
                strokeWidth={inDrawer ? (active ? 1.9 : 1.5) : 1.75}
              />
              <span>{t(item.label)}</span>
            </Link>
          );
        })}
        {drawerFooter}
        {/* 언어 토글 — expandedRail 과 모바일 드로우가 공유하는 본문. */}
        <SidebarLanguageToggle />
        <div className="mt-0.5 flex items-center gap-0.5">
          <VersionBadge />
        </div>
      </div>
    </>
  );

  /* ---- Desktop collapsed rail (≥lg) ---- */
  const collapsedRail = (
    <aside className="group/sb relative hidden h-screen w-16 shrink-0 flex-col items-center border-r border-[var(--border)] bg-[var(--card)] py-3.5 transition-all duration-200 lg:flex">
      {/* Header: logo + collapse toggle (toggle replaces logo on hover) */}
      <div className="relative mb-3.5 flex h-10 w-10 items-center justify-center">
        <Link
          href="/"
          aria-label="AI TokTok"
          className="flex items-center justify-center transition-opacity duration-150 group-hover/sb:opacity-0"
        >
          <Image
            src="/logo-v2.png"
            alt="AI TokTok"
            width={22}
            height={22}
            className="h-[22px] w-[22px] rounded-md"
          />
        </Link>
        <button
          onClick={() => setCollapsed(false)}
          className="absolute inset-0 flex items-center justify-center rounded-lg text-[var(--muted-foreground)] opacity-0 transition-all duration-150 hover:bg-[var(--background)]/60 hover:text-[var(--foreground)] group-hover/sb:opacity-100"
          aria-label={t("Expand sidebar")}
        >
          <PanelLeftOpen size={16} />
        </button>
      </div>

      {/* Primary nav */}
      <nav className="mt-1 flex w-full flex-col items-center gap-1.5 px-1.5">
        {PRIMARY_NAV.map((item) => {
          const active = pathname.startsWith(item.href);
          const locked = navLocked(item);
          const description = locked
            ? lockedTooltip
            : item.tooltipKey
              ? t(item.tooltipKey)
              : undefined;
          if (locked) {
            return (
              <Tooltip
                key={item.href}
                label={t(item.label)}
                description={description}
                side="right"
              >
                <div
                  aria-label={`${t(item.label)} — ${lockedTooltip}`}
                  aria-disabled
                  className="relative flex h-11 w-11 cursor-not-allowed items-center justify-center rounded-lg text-[var(--muted-foreground)]/40"
                >
                  <item.icon size={21} strokeWidth={1.75} />
                  <Lock
                    size={10}
                    strokeWidth={2}
                    className="absolute bottom-1 right-1 text-[var(--muted-foreground)]/70"
                  />
                </div>
              </Tooltip>
            );
          }
          return (
            <Tooltip
              key={item.href}
              label={t(item.label)}
              description={description}
              side="right"
            >
              <Link
                href={item.href}
                onClick={item.href === "/home" ? handleHomeClick : undefined}
                aria-label={t(item.label)}
                className={`relative flex h-11 w-11 items-center justify-center rounded-lg transition-all duration-150 ${
                  active
                    ? "bg-[var(--secondary)] text-[var(--primary)] before:absolute before:-left-2.5 before:top-1/2 before:h-[22px] before:w-[3px] before:-translate-y-1/2 before:rounded-r-[3px] before:bg-[var(--primary)] before:content-['']"
                    : "text-[var(--foreground)]/85 hover:bg-[var(--background)]/60 hover:text-[var(--foreground)]"
                }`}
              >
                <item.icon size={21} strokeWidth={1.75} />
              </Link>
            </Tooltip>
          );
        })}
      </nav>

      <div className="flex-1" />

      {/* Secondary nav + footer */}
      <div className="flex w-full flex-col items-center gap-1.5 px-1.5">
        <div className="my-1 h-px w-8 bg-[var(--border)]/40" />
        {SECONDARY_NAV.map((item) => {
          const active = pathname.startsWith(item.href);
          return (
            <Link
              key={item.href}
              href={item.href}
              title={t(item.label) as string}
              className={`relative flex h-11 w-11 items-center justify-center rounded-lg transition-all duration-150 ${
                active
                  ? "bg-[var(--secondary)] text-[var(--primary)] before:absolute before:-left-2.5 before:top-1/2 before:h-[22px] before:w-[3px] before:-translate-y-1/2 before:rounded-r-[3px] before:bg-[var(--primary)] before:content-['']"
                  : "text-[var(--foreground)]/85 hover:bg-[var(--background)]/60 hover:text-[var(--foreground)]"
              }`}
            >
              <item.icon size={21} strokeWidth={1.75} />
            </Link>
          );
        })}
        {railFooter}
        {/* 접힌 레일 — 아이콘 전용 축약형. */}
        <SidebarLanguageToggle collapsed />
        <VersionBadge collapsed />
      </div>
    </aside>
  );

  /* ---- Desktop expanded rail (≥lg) ---- */
  const expandedRail = (
    <aside className="hidden h-screen w-[220px] shrink-0 flex-col border-r border-[var(--border)] bg-[var(--card)] transition-all duration-200 lg:flex">
      {renderExpandedBody(false)}
    </aside>
  );

  return (
    <>
      {/* Desktop: vertical rail, collapsible (≥lg) */}
      {collapsed ? collapsedRail : expandedRail}

      {/* Mobile: horizontal topbar (<lg) */}
      <header className="flex h-14 shrink-0 items-center gap-1 border-b border-[var(--border)]/60 bg-[var(--secondary)] px-3 lg:hidden">
        <Link
          href="/"
          aria-label="AI TokTok"
          className="mr-1 flex shrink-0 items-center gap-1.5"
        >
          <Image
            src="/logo-v2.png"
            alt="AI TokTok"
            width={22}
            height={22}
            className="h-[22px] w-[22px] rounded-md"
          />
        </Link>
        <nav className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {PRIMARY_NAV.map((item) => {
            const active = pathname.startsWith(item.href);
            const locked = navLocked(item);
            if (locked) {
              return (
                <div
                  key={item.href}
                  aria-label={`${t(item.label)} — ${lockedTooltip}`}
                  aria-disabled
                  title={t(item.label) as string}
                  className="relative flex h-9 shrink-0 cursor-not-allowed items-center gap-1.5 rounded-lg px-2.5 text-[13.5px] text-[var(--muted-foreground)]/40"
                >
                  <item.icon size={17} strokeWidth={1.6} />
                  <span className="hidden sm:inline">{t(item.label)}</span>
                  <Lock size={11} strokeWidth={2} className="shrink-0" />
                </div>
              );
            }
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={item.href === "/home" ? handleHomeClick : undefined}
                aria-current={active ? "page" : undefined}
                title={t(item.label) as string}
                className={`relative flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[13.5px] transition-colors ${
                  active
                    ? "bg-[var(--accent)] font-medium text-[var(--foreground)]"
                    : "text-[var(--foreground)]/85 hover:bg-[var(--background)]/60 hover:text-[var(--foreground)]"
                }`}
              >
                <item.icon size={17} strokeWidth={active ? 2 : 1.6} />
                <span className="hidden sm:inline">{t(item.label)}</span>
              </Link>
            );
          })}
        </nav>
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[var(--foreground)]/85 transition-colors hover:bg-[var(--background)]/60 hover:text-[var(--foreground)]"
          aria-label={t("Open menu")}
        >
          <Menu size={20} strokeWidth={1.8} />
        </button>
      </header>

      {/* Mobile: slide-in drawer holding Recents + secondary nav + footer (<lg) */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label={t("Close menu")}
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
          />
          <aside className="absolute inset-y-0 left-0 flex w-[272px] max-w-[82%] flex-col bg-[var(--secondary)] shadow-2xl">
            {renderExpandedBody(true)}
          </aside>
        </div>
      )}
    </>
  );
}
